import { ChangeEvent, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Button as MuiButton, Grid, Stack, TextField as MuiTextField, Box } from '@mui/material'
import { spacing } from '@mui/system'
import { styled } from '@mui/material/styles'
import { RootState } from '../redux'
import { createLift, updateLift, hideLift } from '../redux/slices/lifts'
import { getProjects } from '../redux/slices/projects'
import { getSectorsByProjectId } from '../redux/slices/sectors'
import useAuth from '../hooks/useAuth'
import useLift from '../hooks/useLift'
import LiftInput from '../types/LiftInput'
import ProjectSelect from './ProjectSelect'
import SectorSelect from './SectorSelect'
import liftService from '../services/liftService'
import { deleteError, setError } from '../redux/slices/errors'
import { LiftErrors } from '../enums/LiftErrors'
import { createErrorOrSuccessNotification } from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { createUserNotification } from '../utils/createUserNotification'
import { Lift } from '../types'
import { LoadingButton } from '@mui/lab'

const Button = styled(MuiButton)(spacing)
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)

export interface LiftFormProps {
  value?: LiftInput
  hidden?: Array<keyof LiftInput>
  disabled?: Array<keyof LiftInput>
  onCancel?: () => void
  onSubmit?: (values: LiftInput, helpers: FormikHelpers<LiftInput>) => void
  beforeSubmit?: (values: LiftInput, helpers: FormikHelpers<LiftInput>) => void
  afterSubmit?: (lift: Lift) => void
}

export default function LiftForm({
  value = {},
  hidden = ['project' as keyof LiftInput],
  disabled = [],
  onCancel,
  onSubmit,
  beforeSubmit,
  afterSubmit
}: LiftFormProps) {
  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The lift.
   */
  const lift: Lift | undefined = useLift()

  const isLoadingProjects: boolean = useSelector((state: RootState) => state.projects.loading)

  const isLoadingSectors: boolean = useSelector((state: RootState) => state.sectors.loading)

  const initialValues: LiftInput =
    lift === undefined
      ? {
          name: '',
          number: 0,
          project: null,
          sector: null,
          ...value
        }
      : {
          name: lift.name,
          number: lift.number,
          project: lift.project?.id ?? null,
          sector: lift.sector?.id ?? null,
          ...value
        }

  const validationSchema = Yup.object().shape({
    // FIXME: Add unique condition:
    // - Name should be unique among lifts of the selected project
    name: Yup.string().required(
      t('isRequired', {
        field: t('Lift.name')
      })
    ),
    number: Yup.number().nullable(),
    project: Yup.number().required(
      t('isRequired', {
        field: t('Lift.project')
      })
    ),
    sector: Yup.number().nullable()
  })

  useEffect(() => {
    dispatch(getProjects())
  }, [])

  useEffect(() => {
    if (lift?.project?.id) {
      dispatch(getSectorsByProjectId(lift.project.id))
    }
  }, [lift])

  function isHidden(field: string): boolean {
    return hidden.includes(field as keyof LiftInput)
  }

  function isDisabled(field: string): boolean {
    return disabled.includes(field as keyof LiftInput)
  }

  async function handleSubmit(
    values: LiftInput,
    { resetForm, setErrors, setStatus, setSubmitting, ...helpers }: FormikHelpers<LiftInput>
  ): Promise<void> {
    try {
      if (onSubmit) {
        return onSubmit(values, {
          resetForm,
          setErrors,
          setStatus,
          setSubmitting,
          ...helpers
        })
      } else {
        if (beforeSubmit) {
          beforeSubmit(values, {
            resetForm,
            setErrors,
            setStatus,
            setSubmitting,
            ...helpers
          })
        }

        let createrOrUpdatedLift: Lift

        if (lift === undefined) {
          const createdLift: Lift = await liftService.createLift(values)

          dispatch(createLift(createdLift))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('liftCreateSuccess'))
          )

          createrOrUpdatedLift = createdLift
        } else {
          const updatedLift: Lift = await liftService.updateLift(lift.id, values)

          dispatch(updateLift(updatedLift))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('liftUpdateSuccess'))
          )

          createrOrUpdatedLift = updatedLift
        }

        resetForm()
        setStatus({ sent: true })
        dispatch(hideLift())

        if (afterSubmit) {
          afterSubmit(createrOrUpdatedLift)
        }
      }
    } catch (error: unknown) {
      setStatus({ sent: false })
      // FIXME: Set validation errors.
      dispatch(
        setError({
          type: lift ? LiftErrors.EDIT : LiftErrors.CREATE,
          error: error
        })
      )

      const errorMessage = createUserNotification({
        user: currentUser,
        type: lift ? LiftErrors.EDIT : LiftErrors.CREATE,
        error: error
      })

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )

      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(lift ? LiftErrors.EDIT : LiftErrors.CREATE))
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel(): void {
    if (onCancel) {
      onCancel()
    } else {
      dispatch(hideLift())
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        setValues,
        errors,
        isSubmitting,
        touched,
        handleBlur,
        handleChange,
        handleSubmit
      }) => (
        <Box mt={2}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={6}>
              {!isHidden('project') && (
                <Grid item xs={12}>
                  <ProjectSelect
                    data-testid="project"
                    name="project"
                    label={t('Lift.project')}
                    value={values.project}
                    disabled={isDisabled('project') || isLoadingProjects || isSubmitting}
                    error={Boolean(touched.project && errors.project)}
                    helperText={touched.project && errors.project}
                    onBlur={handleBlur}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const projectId: number = parseInt(event.target.value)

                      let newValues = {
                        ...values,
                        project: projectId
                      }

                      if (lift?.project?.id !== projectId) {
                        dispatch(getSectorsByProjectId(projectId))
                        newValues = {
                          ...newValues,
                          sector: null
                        }
                      }

                      setValues(newValues)
                    }}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              )}

              {!isHidden('name') && (
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t('Lift.name')}
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    disabled={isDisabled('name') || isSubmitting}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              )}

              {!isHidden('sector') && (
                <Grid item xs={12}>
                  <SectorSelect
                    data-testid="sector"
                    name="sector"
                    label={t('Lift.sector')}
                    value={values.sector}
                    disabled={isDisabled('sector') || isLoadingSectors || isSubmitting}
                    error={Boolean(touched.sector && errors.sector)}
                    helperText={touched.sector && errors.sector}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                  />

                  {values.sector && (
                    <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setValues({
                            ...values,
                            sector: null
                          })
                        }}
                      >
                        {t('clearSelection')}
                      </Button>
                    </Box>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                  <Button type="button" disabled={isSubmitting} onClick={() => handleCancel()}>
                    {t('cancel')}
                  </Button>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={isSubmitting}
                  >
                    {t('save')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Formik>
  )
}
